import { GameState }    from '../../core/GameState.js';
import { EventBus }     from '../../core/EventBus.js';
import { CARGO_TYPES }  from '../../config/cargoTypes.js';
import { SHIP_CATEGORIES } from '../../config/shipTypes.js';
import { formatMoney, formatDuration }  from '../../utils/formatting.js';
import { haversineDistance } from '../../utils/math.js';
import { formatEta, estimateEta } from '../../utils/eta.js';
import { showAlert, showConfirm } from '../components/dialogs.js';
import { serverIntent } from '../../core/serverIntent.js';
import { GAME_CONSTANTS } from '../../config/constants.js';
import { pickShipName }    from '../../config/shipNames.js';
import { describeHandling } from '../../utils/cargoHandling.js';
import { heldEtaChipHtml, heldEtaRowHtml, etaSortHours } from './etaInfo.js';
import { scheduleChipHtml, scheduleRowHtml } from './scheduleInfo.js';
import { routeLoopChipHtml, routeStopsTableHtml } from './routeEtaInfo.js';
import { maintenanceWarningHtml, maintenanceRowsHtml } from './maintenanceInfo.js';

/**
 * Fleet tab: lists all owned ships with status, cargo, and action buttons.
 */
export class FleetPanel {
  constructor(uiManager) {
    this.ui = uiManager;
    this.highlightedPortId = null;
    this._expandedShipId = null; // track open details panel

    // List order: 'default' (ships at the highlighted port first) or 'eta'
    // (soonest arrival first). Remembered per browser — a viewer convenience.
    this.sortBy = 'default';
    try { if (localStorage.getItem('fleetSortBy') === 'eta') this.sortBy = 'eta'; } catch { /* storage unavailable */ }

    // In-progress inline ship rename — survives a render() rebuild
    // triggered by something unrelated (see render()'s _renamingShipId
    // handling and _startRename()'s docblock).
    this._renamingShipId = null;
    this._renamingDraftValue = null;
    this._suppressRenameCommit = false;

    // Listen for port selection to highlight docked ships
    EventBus.on('ui:portSelected', ({ port }) => {
      this.highlightedPortId = port.id;
      // Defer re-render to avoid destroying DOM during click event propagation
      if (this.ui.activeTab === 'fleet') requestAnimationFrame(() => this.render());
    });

    // Crew hire/fire re-renders go through UIManager's activeTab-gated
    // `rerender` wiring (src/ui/UIManager.js) instead of a listener here —
    // this used to call this.render() unconditionally on every
    // staff:crewHired/crewFired, which (unlike the ui:portSelected handler
    // right above) had no `this.ui.activeTab === 'fleet'` guard: hiring
    // crew while on, say, the Market tab would blow away
    // '.panel-content''s actual content and replace it with the Fleet
    // ship list underneath whatever tab was actually showing.
  }

  render() {
    // If a rename <input> is currently focused, rebuilding the DOM below
    // is about to detach it — which fires a real 'blur' event on it, same
    // as the user clicking away. Suppress just that one blur's commit;
    // the input gets recreated with its draft text intact further down.
    if (this._renamingShipId) this._suppressRenameCommit = true;

    const container = document.querySelector('.panel-content');
    let ships = GameState.getAllShips();

    // Sort ships: docked at highlighted port first
    if (this.highlightedPortId) {
      ships = [...ships].sort((a, b) => {
        const aAtPort = (a.status === 'docked' || a.status === 'anchored') && a.currentPortId === this.highlightedPortId;
        const bAtPort = (b.status === 'docked' || b.status === 'anchored') && b.currentPortId === this.highlightedPortId;
        if (aAtPort && !bAtPort) return -1;
        if (!aAtPort && bAtPort) return 1;
        return 0;
      });
    }

    // ETA sort: idle ships (no destination) first, then sailing ships by
    // soonest arrival, then held ships by their real ETA (unknown ETAs last). Ties keep
    // the order above (Array sort is stable)
    if (this.sortBy === 'eta') {
      ships = [...ships].sort((a, b) => {
        const ea = this._etaHours(a), eb = this._etaHours(b);
        return ea === eb ? 0 : ea < eb ? -1 : 1;   // no subtraction: ±Infinity - itself is NaN
      });
    }

    if (ships.length === 0) {
      container.innerHTML = '<p class="panel-empty">No ships yet. Start a new game to receive your starter ship.</p>';
      return;
    }

    const sortBtn = (key, label) =>
      `<button class="bfilter${this.sortBy === key ? ' active' : ''}" data-fleet-sort="${key}">${label}</button>`;
    container.innerHTML = `
      <div class="fleet-sort">Sort: ${sortBtn('default', 'Default')}${sortBtn('eta', 'ETA')}</div>
      <div class="fleet-list"></div>`;
    container.querySelector('.fleet-sort').addEventListener('click', (e) => {
      const btn = e.target.closest('[data-fleet-sort]');
      if (!btn || btn.dataset.fleetSort === this.sortBy) return;
      this.sortBy = btn.dataset.fleetSort;
      try { localStorage.setItem('fleetSortBy', this.sortBy); } catch { /* storage unavailable */ }
      this.render();
    });
    const list = container.querySelector('.fleet-list');

    for (const ship of ships) {
      const card = document.createElement('div');
      const isHighlighted = this.highlightedPortId &&
        (ship.status === 'docked' || ship.status === 'anchored') &&
        ship.currentPortId === this.highlightedPortId;
      card.className = 'ship-card' + (isHighlighted ? ' ship-card-highlighted' : '');
      card.dataset.shipId = ship.id;

      const isStranded = ship.status === 'docked' && !ship.currentPortId;
      const isAnchored = ship.status === 'anchored';
      let queueBadge = '';
      if (isAnchored && ship.currentPortId) {
        const anchorPort = GameState.getPort(ship.currentPortId);
        if (anchorPort) {
          const qPos = anchorPort.anchorQueue.indexOf(ship.id);
          if (qPos !== -1) {
            queueBadge = ` <span class="queue-badge">Queue #${qPos + 1}/${anchorPort.anchorQueue.length}</span>`;
          }
        }
      }

      let statusClass, statusLabel, location;

      if (isStranded) {
        statusClass = 'other';
        statusLabel = 'stranded';
        location = 'Stranded at sea';
      } else if (isAnchored) {
        statusClass = 'other';
        statusLabel = 'anchored';
        const port = GameState.getPort(ship.currentPortId);
        location = port ? `Anchored near ${port.name}` : 'Anchored';
      } else if (ship.status === 'sailing') {
        statusClass = 'sailing';
        statusLabel = 'sailing';
        if (ship.currentRoute) {
          const destName = GameState.getPort(ship.currentRoute.destinationPortId)?.name || '?';
          // Same remaining-distance ETA calculation as the Details view's
          // "Underway"/"ETA" rows (_voyageStatusRowsHtml below) — just
          // surfaced here too so it's visible without opening Details.
          const remaining = ship.currentRoute.distance
            ? ship.currentRoute.distance * (1 - (ship.voyageProgress || 0))
            : null;
          const eta = remaining ? formatEta(remaining, ship.getCurrentSpeed(), GameState.currentDate) : null;
          location = `→ ${destName}${eta ? ` · ETA ${eta}` : ''}`;
        } else {
          location = 'At sea';
        }
      } else if (ship.status === 'docked') {
        if (ship.maintenance?.detention) {
          statusClass = 'detained';
          statusLabel = 'detained';
        } else if (ship.isBusyInPort()) {
          statusClass = 'handling';
          statusLabel = ship.isHandlingCargo() ? (ship.handlingOp || 'loading')
            : ship.getRepairHoursRemaining() > 0 ? 'repairing' : 'refuelling';
        } else {
          statusClass = 'docked';
          statusLabel = 'docked';
        }
        location = GameState.getPort(ship.currentPortId)?.name || 'Unknown';
      } else if (ship.status === 'drydock') {
        statusClass = 'handling';
        statusLabel = 'dry dock';
        location = GameState.getPort(ship.currentPortId)?.name || 'Unknown';
      } else {
        statusClass = 'other';
        statusLabel = ship.status;
        location = ship.currentPortId ? GameState.getPort(ship.currentPortId)?.name || 'Unknown' : 'At sea';
      }

      const assignedRoute = ship.assignedRouteId ? GameState.tradeRoutes?.get(ship.assignedRouteId) : null;

      card.innerHTML = `
        <div class="ship-card-header">
          <strong class="ship-name" data-action="rename" data-ship="${ship.id}" title="Click to rename">${ship.name}</strong>
          <span class="ship-status status-${statusClass}">${statusLabel}</span>
        </div>
        <div class="ship-card-body">
          <span class="ship-type">${ship.type?.name || 'Unknown'}</span>
          <span class="ship-location">${location}</span>
          ${assignedRoute ? `<span class="ship-route-badge">Route: ${assignedRoute.name}</span>` : ''}
          ${describeHandling(ship) ? `<span class="ship-handling${statusClass === 'detained' ? ' ship-handling--detained' : ''}">⏳ ${describeHandling(ship)}</span>` : ''}
          ${heldEtaChipHtml(ship)}${scheduleChipHtml(ship)}${routeLoopChipHtml(ship)}
          ${maintenanceWarningHtml(ship)}
        </div>
        <div class="ship-card-stats">
          <span>Cargo: ${ship.cargoCapacityUsed}/${ship.getCargoCapacity()} TEU</span>
          <span>Fuel: ${Math.round(ship.fuel)}/${ship.maxFuel} t</span>
          <span>Condition: ${Math.round(ship.condition)}%</span>
        </div>
        <div class="ship-card-actions">
          <button class="btn btn-sm" data-action="details" data-ship="${ship.id}">Details</button>
          ${(ship.status === 'docked' && !isStranded) || ship.status === 'drydock' ? `<button class="btn btn-sm btn-primary" data-action="depart" data-ship="${ship.id}">Set Route</button>` : ''}
          ${ship.status === 'docked' && !isStranded && !ship.isHeldInPort() ? `<button class="btn btn-sm" data-action="anchor" data-ship="${ship.id}">Anchor</button>` : ''}
          ${ship.status === 'anchored' ? `<button class="btn btn-sm btn-primary" data-action="dock" data-ship="${ship.id}">Dock</button>${queueBadge}` : ''}
          ${ship.status === 'anchored' ? `<button class="btn btn-sm" data-action="depart" data-ship="${ship.id}">Set Route</button>` : ''}
          ${ship.status === 'sailing' ? `<button class="btn btn-sm btn-primary" data-action="depart" data-ship="${ship.id}">Redirect</button>` : ''}
          ${isStranded ? `<button class="btn btn-sm btn-warning" data-action="tow" data-ship="${ship.id}">Request Tow</button>` : ''}
          ${ship.status === 'docked' && !isStranded ? `<button class="btn btn-sm btn-danger"  data-action="sell"   data-ship="${ship.id}">Sell</button>` : ''}
        </div>`;

      list.appendChild(card);
    }

    // Event delegation
    list.addEventListener('click', (e) => this._handleClick(e));

    // Re-expand details if one was open before render
    if (this._expandedShipId) {
      const ship = GameState.getShip(this._expandedShipId);
      if (ship) this._showDetails(ship, false);
    }

    // Resume an in-progress inline rename across this rebuild — render()
    // gets called for all sorts of reasons unrelated to whatever ship is
    // being renamed (e.g. ANY sailing ship's fuel/condition ticking once a
    // real second — see ship:fuelChanged/ship:conditionChanged), and
    // rebuilding container.innerHTML wholesale destroys the rename
    // <input> along with everything else. Used to make the rename box
    // vanish within about a second of opening it, with no way to actually
    // finish typing a new name. See _startRename()'s _suppressRenameCommit
    // handling for why this doesn't also silently submit a half-typed name.
    if (this._renamingShipId) {
      const ship = GameState.getShip(this._renamingShipId);
      const nameEl = ship
        ? list.querySelector(`[data-action="rename"][data-ship="${this._renamingShipId}"]`)
        : null;
      if (ship && nameEl) {
        this._startRename(nameEl, ship, this._renamingDraftValue);
      } else {
        this._renamingShipId = null;
        this._renamingDraftValue = null;
      }
    }
  }

  /** Sort key for the ETA order: game hours until arrival; -Infinity for
   *  ships with no destination, Infinity when the ETA is unknown. */
  _etaHours(ship) {
    return etaSortHours(ship);
  }

  _handleClick(e) {
    // Clicking the ship name triggers inline rename
    const nameEl = e.target.closest('[data-action="rename"]');
    if (nameEl) {
      const ship = GameState.getShip(nameEl.dataset.ship);
      if (ship) this._startRename(nameEl, ship);
      return;
    }

    // Clicking a button triggers its action
    const btn = e.target.closest('button[data-action]');
    if (btn) {
      const action = btn.dataset.action;
      const shipId = btn.dataset.ship;
      const crewId = btn.dataset.crew;
      const role   = btn.dataset.role;
      const ship   = GameState.getShip(shipId);

      if (action === 'hire-crew' && ship && role) {
        this._showHirePanel(ship, role);
        return;
      }

      if (action === 'fire-crew' && crewId) {
        this._fireCrew(crewId);
        return;
      }

      if (!ship) return;

      switch (action) {
        case 'details': this._showDetails(ship); return;
        case 'depart':  this._showDepartModal(ship); return; // skip _selectShip — it emits ui:portSelected which cancels route selection
        case 'sell':    this._confirmSell(ship); break;
        case 'tow':     this._requestTow(ship); break;
        case 'anchor':  this._anchorShip(ship); break;
        case 'dock':    this._dockShip(ship); break;
      }
      // Also select the ship
      this._selectShip(ship);
      return;
    }

    // Clicking anywhere on the card selects the ship
    const card = e.target.closest('.ship-card');
    if (card) {
      const ship = GameState.getShip(card.dataset.shipId);
      if (ship) this._selectShip(ship);
    }
  }

  _selectShip(ship) {
    this.ui.selectedShip = ship;
    // Highlight selected card
    document.querySelectorAll('.ship-card.selected').forEach(el => el.classList.remove('selected'));
    const card = document.querySelector(`.ship-card[data-ship-id="${ship.id}"]`);
    if (card) card.classList.add('selected');
    // Zoom map to ship location
    EventBus.emit('ui:shipSelected', { ship });
    // If docked, also open the port panel
    if (ship.status === 'docked' && ship.currentPortId) {
      const port = GameState.getPort(ship.currentPortId);
      if (port) EventBus.emit('ui:portSelected', { port });
    }
  }

  /**
   * @param {HTMLElement} el                The ship-name element to replace.
   * @param {Ship} ship
   * @param {string} [draftValue]  When resuming across a render() rebuild
   *   (see render()'s _renamingShipId handling), the text the user had
   *   already typed — restored instead of resetting to ship.name. Omitted
   *   entirely on a fresh open (the user just clicked the name).
   */
  _startRename(el, ship, draftValue) {
    this._renamingShipId = ship.id;
    this._renamingDraftValue = draftValue === undefined ? ship.name : draftValue;

    // Select-all vs. caret-at-end is keyed off whether anything has
    // actually been typed yet (draft still equals ship.name), NOT off
    // whether this happens to be the original click or a render()-forced
    // restore. A restore can easily land before the user's first
    // keystroke (the fuel/condition tick interval is ~1s, well within
    // reach of "just opened the box") — treating every restore as
    // "mid-edit" lost the select-all in that case, so the very next
    // keystroke landed at the end of the OLD name instead of replacing
    // it. Once the draft genuinely diverges from ship.name, THEN a
    // restore should preserve the caret position instead of re-selecting
    // everything out from under whatever the user is mid-typing.
    const hasUnstartedEdit = this._renamingDraftValue === ship.name;

    // Replace the name text with an inline input + random name button
    const wrapper = document.createElement('span');
    wrapper.className = 'ship-rename-wrapper';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'ship-rename-input';
    input.value = this._renamingDraftValue;
    input.maxLength = 30;

    const randomBtn = document.createElement('button');
    randomBtn.className = 'btn btn-xs ship-rename-random';
    randomBtn.textContent = '\u{1F3B2}';
    randomBtn.title = 'Random name';

    wrapper.appendChild(input);
    wrapper.appendChild(randomBtn);
    el.replaceWith(wrapper);
    input.focus();
    if (hasUnstartedEdit) {
      input.select();
    } else {
      // Something's already been typed — place the caret at the end
      // instead of re-selecting everything, so an unrelated render()
      // (a different ship's fuel ticking, say) doesn't clobber it.
      input.setSelectionRange(input.value.length, input.value.length);
    }

    input.addEventListener('input', () => {
      this._renamingDraftValue = input.value;
    });

    const finish = () => {
      this._renamingShipId = null;
      this._renamingDraftValue = null;
    };

    const commit = async () => {
      finish();
      const newName = input.value.trim();
      if (newName && newName !== ship.name) {
        // Used to just mutate ship.name locally and never tell the server
        // at all — looked committed (this.render() picked up the local
        // change) until the next full snapshot/reload silently reverted
        // it, since the server's own copy never changed. serverIntent()
        // persists it; the WS ship:renamed delta it triggers (see
        // GameState.js's applyDelta) is what actually keeps the map
        // marker's label and every other view in sync afterward — this
        // local assignment is just the optimistic step before that lands.
        const result = await serverIntent('ship/rename', { shipId: ship.id, name: newName });
        if (result.success) ship.name = newName;
      }
      // About to detach this same input ourselves via render() — suppress
      // the blur that fires when it does, same reasoning as render()'s own
      // _renamingShipId handling (this is us tidying up, not the user
      // clicking away, and _renamingShipId is already null at this point
      // so render() wouldn't set the flag on its own).
      this._suppressRenameCommit = true;
      this.render();
    };

    randomBtn.addEventListener('mousedown', (e) => {
      e.preventDefault(); // prevent blur on input
      input.value = pickShipName();
      this._renamingDraftValue = input.value;
      input.focus();
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      if (e.key === 'Escape') {
        e.preventDefault();
        finish();
        this._suppressRenameCommit = true; // same reasoning as commit() above
        this.render();
      }
    });
    input.addEventListener('blur', (e) => {
      // render() detaching this input to rebuild the ship list (a totally
      // unrelated ship's fuel/condition tick, most commonly) fires a real
      // blur event too — that's not the user clicking away, so don't treat
      // it as one. render() itself resumes the edit right after; only
      // clear the flag here, don't touch renaming state.
      if (this._suppressRenameCommit) { this._suppressRenameCommit = false; return; }
      // Don't commit if clicking the random button
      if (e.relatedTarget === randomBtn) return;
      commit();
    });
  }

  _anchorShip(ship) {
    if (ship.status !== 'docked' || !ship.currentPortId) return;

    const port = GameState.getPort(ship.currentPortId);
    if (!port) return;

    // Remove from docked list (frees a berth)
    port.dockedShips.delete(ship.id);

    // Set anchored status — manual anchor does NOT enter queue
    ship.status = 'anchored';
    ship.dockedSince = null;

    EventBus.emit('ui:notification', {
      message: `${ship.name} anchored in roadstead near ${port.name}`,
      type: 'info'
    });

    EventBus.emit('port:berthFreed', { port });
    this.render();
  }

  _dockShip(ship) {
    if (ship.status !== 'anchored' || !ship.currentPortId) return;

    const port = GameState.getPort(ship.currentPortId);
    if (!port) return;

    const berthsFull = !port.hasBerthAvailable();
    const baseFee = port.dockingFee;
    const fee = berthsFull ? Math.round(baseFee * GAME_CONSTANTS.BERTH_OVERAGE_MULTIPLIER) : baseFee;

    if (berthsFull) {
      const surcharge = fee - baseFee;
      showConfirm(
        `${port.name} berths are full (${port.dockedShips.size}/${port.berthCount}).<br>` +
        `Docking fee: ${formatMoney(baseFee)} + ${formatMoney(surcharge)} overage surcharge = <strong>${formatMoney(fee)}</strong>`,
        { title: 'Force Dock?', confirmLabel: 'Dock with surcharge' }
      ).then(ok => {
        if (!ok) return;
        this._executeDock(ship, port, fee);
      });
    } else {
      if (!GameState.player.canAfford(fee)) {
        showAlert(`Cannot afford docking fee: ${formatMoney(fee)}`);
        return;
      }
      this._executeDock(ship, port, fee);
    }
  }

  async _executeDock(ship, port, fee) {
    if (!GameState.player.canAfford(fee)) {
      showAlert(`Cannot afford docking fee: ${formatMoney(fee)}`);
      return;
    }
    // Send dock intent to server — state update arrives via WS delta
    await serverIntent('ship/dock', { shipId: ship.id, portId: port.id });
    // Local optimistic UI: re-render after intent (server will push delta)
    this.render();
  }

  _findNearestPort(ship) {
    let best = null;
    let bestDist = Infinity;
    for (const port of GameState.getAllPorts()) {
      if (!port.canAcceptShip(ship.type)) continue;
      const d = haversineDistance(ship.position, port.position);
      if (d < bestDist) {
        bestDist = d;
        best = port;
      }
    }
    return best ? { port: best, distance: bestDist } : null;
  }

  /**
   * "From" / "Underway" / "ETA" rows for the Current Status table — only
   * while actually sailing. All derived from data already on the ship
   * (currentRoute.originPortId set at departure — see NavigationSystem.js's
   * departShip(); voyageProgress; currentRoute.distance) rather than any
   * new persisted field: elapsed/remaining distance are just voyageProgress
   * split across the route's total distance, matching exactly what main.js's
   * client-side interpolation is already tracking as this ship's progress.
   */
  _voyageStatusRowsHtml(ship) {
    if (ship.status !== 'sailing' || !ship.currentRoute?.distance) return heldEtaRowHtml(ship) + scheduleRowHtml(ship);

    const originPort = GameState.getPort(ship.currentRoute.originPortId);
    const fromRow = originPort
      ? `<tr><td>From</td><td>${originPort.name}</td></tr>`
      : '';

    const progress = ship.voyageProgress || 0;
    const speed = ship.getCurrentSpeed();

    const elapsed = estimateEta(ship.currentRoute.distance * progress, speed, GameState.currentDate);
    const underwayRow = elapsed
      ? `<tr><td>Underway</td><td>${formatDuration(elapsed.days)}</td></tr>`
      : '';

    const eta = formatEta(ship.currentRoute.distance * (1 - progress), speed, GameState.currentDate);
    const etaRow = eta ? `<tr><td>ETA</td><td>${eta}</td></tr>` : '';

    return fromRow + underwayRow + etaRow + scheduleRowHtml(ship);
  }

  _towCost(ship, distance) {
    // Tow fee must be punitive — more expensive than the fuel would have cost.
    // Base fee + 2x estimated fuel cost for the distance.
    const capacity = ship.type?.cargoCapacity || 50;
    const baseFee = Math.round(5000 + 1000 * Math.sqrt(capacity));
    const fuelPerDay = ship.getFuelConsumptionPerDay?.() || (ship.type?.fuelConsumption || 5);
    const speedNmPerDay = (ship.type?.cruiseSpeed || 10) * 24;
    const travelDays = distance / speedNmPerDay;
    const fuelNeeded = fuelPerDay * travelDays;
    const estFuelCost = fuelNeeded * 500; // average fuel price
    return baseFee + Math.round(estFuelCost * 2);
  }

  _requestTow(ship) {
    const result = this._findNearestPort(ship);
    if (!result) {
      EventBus.emit('ui:notification', { message: 'No reachable port found!', type: 'error' });
      return;
    }
    const { port, distance } = result;
    const cost = this._towCost(ship, distance);

    const canPay = GameState.player.canAfford(cost);
    const msg = `Tow ${ship.name} to ${port.name} (${Math.round(distance)} nm)?` +
      `\n\nTow fee: ${formatMoney(cost)} (emergency rate)` +
      `\nYour funds: ${formatMoney(GameState.player.money)}` +
      (canPay ? '' : '\n\nYou cannot afford this! The fee will be deducted as debt.');

    showConfirm(msg, { title: 'Tow Ship', confirmLabel: 'Tow' }).then(ok => {
      if (!ok) return;
      GameState.player.deductMoney(cost, `Tow to ${port.name}`);

      // Move ship to port
      ship.position = { ...port.position };
      ship.currentPortId = port.id;
      ship.currentRoute = null;
      ship.voyageProgress = 0;
      ship.fuel = Math.max(ship.fuel, 5); // Tug gives a splash of fuel

      if (port.hasBerthAvailable()) {
        ship.status = 'docked';
        ship.dockedSince = GameState.currentDate.toISOString();
        port.dockedShips.add(ship.id);
      } else {
        ship.status = 'anchored';
        ship.dockedSince = null;
        port.enqueueShip(ship.id);
      }

      EventBus.emit('ship:arrived', { ship, port, anchored: ship.status === 'anchored' });
      EventBus.emit('ui:notification', {
        message: `${ship.name} towed to ${port.name}`,
        type: 'success'
      });
      this.render();
    });
  }

  _showDetails(ship, toggle = true) {
    // Toggle: if already expanded, collapse (only on explicit user click)
    const existing = document.querySelector(`.ship-details[data-detail-ship="${ship.id}"]`);
    if (existing) {
      if (toggle) { existing.remove(); this._expandedShipId = null; }
      return;
    }

    // Collapse any other open details
    document.querySelectorAll('.ship-details').forEach(el => el.remove());
    this._expandedShipId = ship.id;

    const card = document.querySelector(`.ship-card[data-ship-id="${ship.id}"]`);
    if (!card) return;

    const t = ship.type || {};
    const catLabel = SHIP_CATEGORIES[t.category]?.label || t.category || '—';
    const restrictions = t.cargoRestrictions
      ? t.cargoRestrictions.map(id => CARGO_TYPES[id]?.name || id).join(', ')
      : 'General cargo';

    // Cargo manifest
    let cargoHtml = '';
    if (ship.cargo.length > 0) {
      cargoHtml = ship.cargo.map(c => {
        const def = CARGO_TYPES[c.type];
        const contractBadge = c.contractId ? ' <span class="cargo-contract-badge">Contract</span>' : '';
        const costInfo = c.purchasePrice ? ` <span class="text-muted">@ ${formatMoney(c.purchasePrice)}/TEU</span>` : '';
        return `<div class="fleet-cargo-row">${c.quantity} TEU <strong>${def?.name || c.type}</strong>${costInfo}${contractBadge}</div>`;
      }).join('');
    } else {
      cargoHtml = '<span class="text-muted">Empty</span>';
    }

    // Crew section
    const maxSlots = t.extraCrewSlots || 0;
    const navCrew = ship.crew.navigator ? GameState.crewMembers.get(ship.crew.navigator) : null;
    const engCrew = ship.crew.engineer ? GameState.crewMembers.get(ship.crew.engineer) : null;
    const usedSlots = (navCrew ? 1 : 0) + (engCrew ? 1 : 0);

    let crewHtml = '';
    if (maxSlots === 0) {
      crewHtml = '<span class="text-muted">No specialist crew slots</span>';
    } else {
      crewHtml = `<div class="crew-slots-summary">Crew Slots: ${usedSlots}/${maxSlots}</div>`;
      crewHtml += this._renderCrewSlot(ship, 'navigator', navCrew);
      crewHtml += this._renderCrewSlot(ship, 'engineer', engCrew);
    }

    // Resale value
    const basePrice = t.basePurchasePrice || 100000;
    const resaleValue = Math.round(basePrice * (0.3 + (ship.condition / 100) * 0.4) * 0.90);

    const detailEl = document.createElement('div');
    detailEl.className = 'ship-details';
    detailEl.dataset.detailShip = ship.id;
    detailEl.innerHTML = `
      <div class="ship-details-grid">
        <div class="ship-details-col">
          <h6>Vessel Specs</h6>
          <table class="stats-table">
            <tr><td>Type</td><td>${t.name || '—'}</td></tr>
            <tr><td>Class</td><td>${catLabel}</td></tr>
            <tr><td>Capacity</td><td>${(t.cargoCapacity || 0).toLocaleString()} TEU</td></tr>
            <tr><td>Speed</td><td>${t.cruiseSpeed || 0} kn (max ${t.maxSpeed || 0})</td></tr>
            <tr><td>Range</td><td>${t.range ? t.range.toLocaleString() + ' nm' : '—'}</td></tr>
            <tr><td>Fuel Tank</td><td>${(t.maxFuel || 0).toLocaleString()} t</td></tr>
            <tr><td>Fuel/day</td><td>${t.fuelConsumption || 0} t</td></tr>
            <tr><td>Daily Cost</td><td>${formatMoney(t.dailyOperatingCost || 0)}</td></tr>
            <tr><td>Crew</td><td>${t.minCrewRequired || 0}</td></tr>
            <tr><td>Cargo Types</td><td>${restrictions}</td></tr>
          </table>
          <h6>Maintenance</h6>
          <table class="stats-table">${maintenanceRowsHtml(ship)}</table>
          <h6>Crew</h6>
          ${crewHtml}
        </div>
        <div class="ship-details-col">
          <h6>Current Status</h6>
          <table class="stats-table">
            <tr><td>Condition</td><td>${Math.round(ship.condition)}%</td></tr>
            <tr><td>Fuel</td><td>${Math.round(ship.fuel)} / ${ship.maxFuel} t</td></tr>
            <tr><td>Cargo</td><td>${ship.cargoCapacityUsed} / ${ship.getCargoCapacity()} TEU</td></tr>
            <tr><td>Eff. Speed</td><td>${ship.getCurrentSpeed().toFixed(1)} kn</td></tr>
            <tr><td>Eff. Fuel/day</td><td>${ship.getFuelConsumptionPerDay().toFixed(1)} t</td></tr>
            ${this._voyageStatusRowsHtml(ship)}
          </table>
          ${routeStopsTableHtml(ship)}
          <h6>Cargo Hold</h6>
          ${cargoHtml}
          <h6>Statistics</h6>
          <table class="stats-table">
            <tr><td>Voyages</td><td>${ship.stats.totalVoyages}</td></tr>
            <tr><td>Distance</td><td>${Math.round(ship.stats.totalDistanceTraveled).toLocaleString()} nm</td></tr>
          </table>
          <div class="ship-details-value">Broker value: <strong>${formatMoney(resaleValue)}</strong></div>
        </div>
      </div>`;

    card.after(detailEl);
  }

  _renderCrewSlot(ship, role, crewMember) {
    const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);

    if (crewMember) {
      const tierClass = `badge-${crewMember.skillTier}`;
      const remaining = crewMember.getContractRemainingDays(GameState.currentDate);
      const penalty = crewMember.getTerminationPenalty(GameState.currentDate);
      const contractLabel = remaining > 0
        ? `<span class="crew-contract-info">${remaining}d left</span>`
        : '<span class="crew-contract-expired">Expired</span>';
      const penaltyLabel = penalty > 0
        ? `<span class="crew-penalty-info">Penalty: ${formatMoney(penalty)}</span>`
        : '';
      return `
        <div class="crew-slot">
          <div class="crew-slot-top">
            <span class="crew-role">${roleLabel}</span>
            <span class="crew-name">${crewMember.name}</span>
            <span class="badge ${tierClass}">${crewMember.skillTier}</span>
            <span class="text-muted">${formatMoney(crewMember.dailySalary)}/day</span>
          </div>
          <div class="crew-slot-bottom">
            ${contractLabel}
            ${penaltyLabel}
            <button class="btn btn-xs btn-danger" data-action="fire-crew" data-crew="${crewMember.id}">Fire</button>
          </div>
        </div>`;
    } else {
      const isDocked = ship.status === 'docked' && ship.currentPortId;
      const hireLabel = isDocked ? 'Hire at Port' : 'Dock to hire';
      return `
        <div class="crew-slot" data-crew-slot="${ship.id}-${role}">
          <span class="crew-role">${roleLabel}</span>
          <span class="text-muted">Empty</span>
          ${isDocked
            ? `<button class="btn btn-xs btn-primary" data-action="hire-crew" data-ship="${ship.id}" data-role="${role}">${hireLabel}</button>`
            : `<span class="text-muted crew-dock-hint">${hireLabel}</span>`
          }
        </div>`;
    }
  }

  _showHirePanel(ship, role) {
    if (!ship.currentPortId) {
      showAlert('Ship must be docked at a port to hire crew.');
      return;
    }
    const port = GameState.getPort(ship.currentPortId);
    if (port) {
      EventBus.emit('ui:portSelected', { port, tab: 'services', highlightCrew: true });
    }
  }

  async _fireCrew(crewId) {
    const crew = GameState.crewMembers.get(crewId);
    if (!crew) return;

    const penalty = crew.getTerminationPenalty(GameState.currentDate);
    const penaltyText = penalty > 0
      ? `<br><br>Early termination penalty: <strong>${formatMoney(penalty)}</strong>`
      : '<br><br>Contract expired — no penalty.';

    showConfirm(
      `Fire ${crew.name} (${crew.role}, ${crew.skillTier})?${penaltyText}`,
      { title: 'Fire Crew', confirmLabel: penalty > 0 ? `Fire (${formatMoney(penalty)})` : 'Fire' }
    ).then(async ok => {
      if (!ok) return;
      const result = await serverIntent('crew/fire', { crewId });
      if (result.success) {
        const msg = result.penalty > 0
          ? `${crew.name} dismissed — penalty ${formatMoney(result.penalty)}`
          : `${crew.name} dismissed`;
        EventBus.emit('ui:notification', {
          message: msg,
          type: 'info'
        });
        this.render();
      }
    });
  }

  _showDepartModal(ship) {
    // Start map-based route selection
    this.ui.startRouteSelection(ship);
  }

  _confirmSell(ship) {
    // Broker-consistent pricing: condition-based + 10% broker commission
    const basePrice = ship.type?.basePurchasePrice || 100000;
    const conditionFactor = 0.3 + (ship.condition / 100) * 0.4;
    const resaleValue = Math.round(basePrice * conditionFactor * 0.90);

    if (ship.cargo.length > 0) {
      showAlert('Unload all cargo before selling.');
      return;
    }

    showConfirm(
      `Sell ${ship.name} for ${formatMoney(resaleValue)}?<br><small>Broker offer based on ${Math.round(ship.condition)}% condition</small>`,
      { title: 'Sell Ship', confirmLabel: 'Sell' }
    ).then(async ok => {
      if (!ok) return;
      await serverIntent('ship/sell', { shipId: ship.id });
      // State update arrives via WS delta (ship:sold)
    });
  }
}
